// src/components/files/FileDetailSlideOver.tsx
// File detail and edit slide-over panel

import { useState, useEffect } from 'react';
import { useGetFileQuery, useUpdateFileMutation, useDeleteFileMutation, useShareFileMutation } from '../../api/filesApi';
import { useGetContactsQuery } from '../../api/contactsApi';
import { useActiveWorkspace } from '../../hooks/useActiveWorkspace';
import SlideOver from '../ui/SlideOver';
import Button from '../ui/Button';
import LoadingSpinner from '../common/LoadingSpinner';
import { DeleteConfirmDialog } from '../ui/ConfirmDialog';

function formatFileSize(bytes: number | null): string {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

interface FileDetailSlideOverProps {
  fileId: string | null;
  onClose: () => void;
}

export default function FileDetailSlideOver({ fileId, onClose }: FileDetailSlideOverProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [showShareForm, setShowShareForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [formData, setFormData] = useState({
    description: '',
    contactId: '',
  });
  const [shareFormData, setShareFormData] = useState({
    toContactId: '',
    message: '',
  });

  // Replaced with useActiveWorkspace
  const activeWorkspace = useActiveWorkspace();
  const workspaceId = activeWorkspace?.workspace.id;

  const { data: file, isLoading, error } = useGetFileQuery(fileId!, { skip: !fileId });
  const { data: contactsResponse } = useGetContactsQuery({ workspaceId }, { skip: !workspaceId });
  const contacts = contactsResponse?.contacts;

  const [updateFile, { isLoading: isUpdating }] = useUpdateFileMutation();
  const [deleteFile, { isLoading: isDeleting }] = useDeleteFileMutation();
  const [shareFile, { isLoading: isSharing }] = useShareFileMutation();

  // Initialize form data when file loads
  useEffect(() => {
    if (file) {
      setFormData({
        description: file.description || '',
        contactId: file.contactId || '',
      });
    }
  }, [file]);

  const handleSave = async () => {
    if (!fileId) return;

    try {
      await updateFile({
        id: fileId,
        description: formData.description || undefined,
        contactId: formData.contactId || undefined,
      }).unwrap();
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update file:', error);
    }
  };

  const handleCancel = () => {
    if (file) {
      setFormData({
        description: file.description || '',
        contactId: file.contactId || '',
      });
    }
    setIsEditing(false);
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!fileId) return;
    try {
      await deleteFile(fileId).unwrap();
      setShowDeleteConfirm(false);
      onClose();
    } catch (error) {
      console.error('Failed to delete file:', error);
    }
  };

  const handleShare = async () => {
    if (!fileId || !shareFormData.toContactId) return;
    try {
      await shareFile({
        id: fileId,
        toContactId: shareFormData.toContactId,
        message: shareFormData.message || undefined,
      }).unwrap();
      setShareFormData({ toContactId: '', message: '' });
      setShowShareForm(false);
    } catch (error) {
      console.error('Failed to share file:', error);
    }
  };

  return (
    <SlideOver
      isOpen={!!fileId}
      onClose={onClose}
      title={isEditing ? 'Edit File' : 'File Details'}
      size="md"
      footer={
        isEditing ? (
          <>
            <Button variant="ghost" onClick={handleCancel} disabled={isUpdating}>
              Cancel
            </Button>
            <Button onClick={handleSave} isLoading={isUpdating}>
              Save Changes
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="ghost"
              onClick={handleDeleteClick}
              isLoading={isDeleting}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              Delete
            </Button>
            <Button variant="secondary" onClick={() => setShowShareForm(!showShareForm)}>
              {showShareForm ? 'Cancel Share' : 'Share'}
            </Button>
            <Button onClick={() => setIsEditing(true)}>Edit</Button>
          </>
        )
      }
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <p className="text-red-500">Failed to load file details.</p>
        </div>
      ) : file ? (
        <div className="space-y-6">
          {/* File Icon and Name */}
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg className="w-7 h-7 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-semibold text-gray-900 break-all">{file.originalName}</h3>
              <p className="text-sm text-gray-500">{formatFileSize(file.sizeBytes)}</p>
              {file.mimeType && <p className="text-xs text-gray-400">{file.mimeType}</p>}
            </div>
          </div>

          {/* Share Form (when visible) */}
          {showShareForm && (
            <div className="p-4 bg-blue-50 rounded-lg space-y-3">
              <h4 className="text-sm font-medium text-gray-900">Share with Contact</h4>
              <select
                value={shareFormData.toContactId}
                onChange={(e) => setShareFormData({ ...shareFormData, toContactId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Select a contact</option>
                {contacts?.map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {contact.fullName}
                  </option>
                ))}
              </select>
              <textarea
                value={shareFormData.message}
                onChange={(e) => setShareFormData({ ...shareFormData, message: e.target.value })}
                placeholder="Add a message (optional)"
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <Button
                onClick={handleShare}
                isLoading={isSharing}
                disabled={!shareFormData.toContactId}
                className="w-full"
              >
                Send
              </Button>
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            {isEditing ? (
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Add a description..."
              />
            ) : (
              <p className="text-gray-900">{file.description || 'No description'}</p>
            )}
          </div>

          {/* Related Contact */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Related Contact</label>
            {isEditing ? (
              <select
                value={formData.contactId}
                onChange={(e) => setFormData({ ...formData, contactId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">None</option>
                {contacts?.map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {contact.fullName}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-gray-900">{file.contact?.fullName || 'None'}</p>
            )}
          </div>

          {/* File Info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Storage Key</p>
              <p className="font-medium text-gray-900 truncate">{file.storageKey}</p>
            </div>
            <div>
              <p className="text-gray-500">Uploaded By</p>
              <p className="font-medium text-gray-900">{file.uploader?.fullName || 'Unknown'}</p>
            </div>
          </div>

          {/* Metadata */}
          <div className="pt-4 border-t border-gray-200 text-xs text-gray-500">
            <p>Created: {new Date(file.createdAt).toLocaleString()}</p>
            <p>Updated: {new Date(file.updatedAt).toLocaleString()}</p>
          </div>
        </div>
      ) : null}

      <DeleteConfirmDialog
        isOpen={showDeleteConfirm}
        itemName={file?.originalName}
        itemType="file"
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </SlideOver>
  );
}
